
import Vue from 'vue';
import _omit from 'lodash/omit';
import { Team } from '@run-diffusion/shared';
import { db } from '@/firebase';
import { mapGetters, mapState } from 'vuex';
import { TeamUserMixin } from '@/mixins';
import { TEAMS_API_BASE_URL, TeamsSummary, TeamsSummaryResult, fetchTeamsSummary, teamsSummaryFailure } from '@/utils/teamsApi';
import { InternalApiCredentials } from '@/utils/internalApi';
import { forgetWebClientDeviceIfRejected, internalApiCredentials } from '@/services/internalApiCredentials';
import { MIRROR_LAG_REFETCH_MS } from '@/utils/cursorWalk';
import { TEAMS_CHANGED } from '@/utils/teamsMirrorLag';
import ConfirmDialog from '@/components/base/ConfirmDialog.vue';
import CompanyOverview from '@/views/Team/CompanyDashboard/CompanyOverview.vue';
import TeamsManagement from '@/views/Team/CompanyDashboard/TeamsManagement.vue';
import { RunnitsOwnerSelection } from '@/store';
import { RUNNITS_OWNER_SELECTION } from '@/views/Runnits/constants';

/**
 * The company dashboard home: the metric cards, and the table of the company's
 * teams under them. The table pages itself from Postgres (TeamsManagement): the
 * active teams, or the deactivated ones under `?deactivated`. The count cards
 * come from the internal `GET /api/v2/internal/teams/summary` (the web app's own,
 * undocumented, answered only for this browser's web client device), which
 * counts both halves in Postgres within the same scope, so the cards show the
 * half the table shows. Both trail
 * Firestore by the mirror's lag: a change made anywhere in the app, an edit as
 * much as an addition (noted on `TEAMS_CHANGED`, `teamsMirrorLag.ts`), asks for
 * the counts again once the mirror has the write. Every table event is
 * re-emitted unchanged for TeamPage, which owns the dialogs; `edit-teams`
 * carries `pagination` because EditTeams orders the selected teams by it.
 */
export default Vue.extend({
	name: 'CompanyDashboard',
	mixins: [
		TeamUserMixin,
	],
	metaInfo () {
		return {
			title: `Dashboard | ${this.team.company?.name || this.team?.name || this.displayLabels.team} | RunDiffusion`,
		};
	},
	data () {
		return {
			joinTeamSuccessDialogOpen: false,

			// The count cards' numbers, the last good ones kept while a refetch runs
			teamsSummary: null as TeamsSummary | null,
			loadingTeamsSummary: false,
			// Bumped on every request so a slow, superseded answer is dropped
			teamsSummaryRequestVersion: 0,
			// The one pending catch-up refetch after a change, if any
			teamsSummaryRefetchTimer: null as number | null,
			stopListeningToTeamsChanges: null as (() => void) | null,
			isDestroyed: false,
		};
	},
	beforeDestroy () {
		this.isDestroyed = true;
		this.clearTeamsSummaryRefetch();
		if (this.stopListeningToTeamsChanges) this.stopListeningToTeamsChanges();
	},
	async created () {
		// A Team or membership changed anywhere (this table, a dialog, the account menu's New Team):
		// count again once the mirror has it
		this.stopListeningToTeamsChanges = TEAMS_CHANGED.onChanged(() => this.refetchTeamsSummaryAfterMirrorLag());

		// set visitedTeamPage
		if (this.user && !this.user.visitedTeamPage) {
			const userRef = db.doc(`users/${this.user.id}`);
			await userRef.update({
				visitedTeamPage: true,
			});
		}
	},
	watch: {
		team: {
			immediate: true,
			handler (newVal: Team, oldVal: Team) {
				const newTeamId: string = newVal?.id || null
				const oldTeamId: string = oldVal?.id || null
				if (newTeamId && newTeamId !== oldTeamId) {
					this.initJoinTeamSuccessDialog(newVal);
				}

				// Handle team loading when companyId changes
				const newCompanyId: string = newVal?.companyId || null;
				const oldCompanyId: string = oldVal?.companyId || null;
				if (newCompanyId !== oldCompanyId) {
					// Another company's numbers are not this one's, not even for a moment
					this.teamsSummary = null;
					this.loadTeamsSummary();
					// A change made just before arriving (on a team's or a member's page, or in the
					// directory) is not in the mirror yet: ask once more when the mirror will have it
					const remainingMs: number = TEAMS_CHANGED.remainingMs();
					if (remainingMs > 0) this.refetchTeamsSummaryAfterMirrorLag(remainingMs);
				}
			},
		},
		// Covers every way the query can change, including browser back and forward
		showingDeactivated (newVal: boolean, oldVal: boolean) {
			if (newVal !== oldVal) {
				// The other half's numbers came with the last answer; ask again so they are fresh
				this.loadTeamsSummary();
			}
		},
	},
	computed: {
		...mapState([
			'user',
			'team',
			'runnitState',
		]),
		...mapGetters([
			'displayLabels',
		]),
		showingDeactivated (): boolean {
			return !!this.$route.query.deactivated;
		},
	},
	methods: {
		/**
		 * The count cards' numbers. A failure keeps the last good numbers on
		 * screen, and with none the cards show a dash rather than a 0 that
		 * reads as real.
		 */
		async loadTeamsSummary (): Promise<void> {
			const companyId: string | null = this.team?.companyId || null;
			const version: number = ++this.teamsSummaryRequestVersion;
			if (!companyId) {
				this.loadingTeamsSummary = false;
				this.teamsSummary = null;
				return;
			}
			this.loadingTeamsSummary = true;
			let result: TeamsSummaryResult;
			try {
				const credentials: InternalApiCredentials | null = await internalApiCredentials();
				result = credentials
					? await fetchTeamsSummary({
						baseUrl: TEAMS_API_BASE_URL,
						...credentials,
						request: { companyId, accountTeamId: this.team?.id || null },
					})
					: { ok: false, reason: 'unauthorized' };
			} catch (e) {
				console.error('Error loading the teams summary:', e);
				result = { ok: false, reason: 'failed' };
			}
			// A newer request owns the cards, and a page that is gone owns nothing
			if (this.isDestroyed || version !== this.teamsSummaryRequestVersion) return;
			this.loadingTeamsSummary = false;
			if (result.ok === true) {
				this.teamsSummary = result.summary;
			} else {
				console.error('Error loading the teams summary:', result);
				// A device the server no longer takes is forgotten, so the next ask registers a new one
				forgetWebClientDeviceIfRejected(teamsSummaryFailure(result)?.code);
			}
		},
		// At most one pending: a repeat call restarts it, so it fires a full lag after the latest change
		refetchTeamsSummaryAfterMirrorLag (delayMs: number = MIRROR_LAG_REFETCH_MS) {
			if (this.isDestroyed) return;
			this.clearTeamsSummaryRefetch();
			this.teamsSummaryRefetchTimer = window.setTimeout(() => {
				this.teamsSummaryRefetchTimer = null;
				if (this.isDestroyed) return;
				this.loadTeamsSummary();
			}, delayMs);
		},
		clearTeamsSummaryRefetch () {
			if (this.teamsSummaryRefetchTimer === null) return;
			window.clearTimeout(this.teamsSummaryRefetchTimer);
			this.teamsSummaryRefetchTimer = null;
		},
		initJoinTeamSuccessDialog (team: Team) {
			if (team && this.$route.query.finishedConnectingTeam) {
				this.joinTeamSuccessDialogOpen = true;
				this.routerReplace(this.$route, this.$router, {
					name: this.$route.name,
					query: _omit(this.$route.query, 'finishedConnectingTeam'),
				});

				// Select team owner selection
				const runnitsOwnerSelection: RunnitsOwnerSelection = RUNNITS_OWNER_SELECTION.TEAM;
				if (this.runnitState.runnitsOwnerSelection !== runnitsOwnerSelection) {
					this.switchBetweenTeamAndUser(runnitsOwnerSelection);
				}
			}
		},
	},
	components: {
		CompanyOverview,
		ConfirmDialog,
		TeamsManagement,
	},
});
